import React, { useState } from 'react';
import {
  Building2, Home, ClipboardList, Camera, Bell, ChevronRight,
  Search, Filter, Plus, Upload, ArrowLeft, Calendar, Clock,
  MapPin, User, Sparkles, TrendingUp, CheckCircle2, Circle,
  DoorOpen, DoorClosed, ArrowRightLeft, X, FileText, Eye,
  LayoutGrid, List, ChevronDown, BedDouble, Bath, Ruler,
  Image, AlertCircle, Check, MoreVertical, Phone, Mail
} from 'lucide-react';
import './PropertyManagerPortal.css';

// ============================================================
// MOCK DATA
// ============================================================
const PROPERTY = {
  name: 'The Meridian at Westfield',
  address: '4200 Westfield Blvd, Suite 100, Indianapolis, IN 46250',
  totalUnits: 156,
  manager: 'Jessica Torres',
  managerEmail: 'jtorres@meridianwestfield.com',
  managerPhone: '(317) 555-0142'
};

const FLOOR_PLANS = [
  { id: 'A1', name: 'The Aspen', beds: 1, baths: 1, sqft: 725 },
  { id: 'A2', name: 'The Birch', beds: 1, baths: 1, sqft: 810 },
  { id: 'B1', name: 'The Cedar', beds: 2, baths: 1, sqft: 980 },
  { id: 'B2', name: 'The Dogwood', beds: 2, baths: 2, sqft: 1100 },
  { id: 'C1', name: 'The Elm', beds: 3, baths: 2, sqft: 1350 },
];

const UNITS = [
  { id: '101', number: '101', building: 'A', floor: 1, planId: 'A1', status: 'occupied', lastCleaned: '2025-12-15', tenant: 'M. Johnson' },
  { id: '102', number: '102', building: 'A', floor: 1, planId: 'A2', status: 'vacant', lastCleaned: '2026-02-10', tenant: null },
  { id: '103', number: '103', building: 'A', floor: 1, planId: 'B1', status: 'turnover', lastCleaned: null, tenant: null },
  { id: '201', number: '201', building: 'A', floor: 2, planId: 'B2', status: 'occupied', lastCleaned: '2026-01-20', tenant: 'S. Williams' },
  { id: '202', number: '202', building: 'A', floor: 2, planId: 'A1', status: 'occupied', lastCleaned: '2025-11-30', tenant: 'K. Davis' },
  { id: '203', number: '203', building: 'A', floor: 2, planId: 'C1', status: 'vacant', lastCleaned: '2026-02-18', tenant: null },
  { id: '301', number: '301', building: 'B', floor: 3, planId: 'B1', status: 'occupied', lastCleaned: '2026-01-05', tenant: 'R. Thompson' },
  { id: '302', number: '302', building: 'B', floor: 3, planId: 'A2', status: 'turnover', lastCleaned: null, tenant: null },
  { id: '303', number: '303', building: 'B', floor: 3, planId: 'B2', status: 'occupied', lastCleaned: '2026-02-01', tenant: 'A. Garcia' },
  { id: '401', number: '401', building: 'B', floor: 4, planId: 'C1', status: 'occupied', lastCleaned: '2025-12-22', tenant: 'L. Martinez' },
  { id: '402', number: '402', building: 'B', floor: 4, planId: 'A1', status: 'vacant', lastCleaned: '2026-02-20', tenant: null },
  { id: '403', number: '403', building: 'B', floor: 4, planId: 'B1', status: 'occupied', lastCleaned: '2026-01-15', tenant: 'T. Anderson' },
];

const MOCK_JOBS = [
  {
    id: 'J001',
    unit: '103',
    building: 'A',
    planId: 'B1',
    type: 'move-out',
    status: 'in-progress',
    priority: 'urgent',
    requestedDate: '2026-02-22',
    scheduledDate: '2026-02-24',
    completedDate: null,
    notes: 'Tenant had 2 cats. Extra attention to carpets and baseboards. Check for any pet damage.',
    photos: { before: 4, after: 0 },
    timeline: [
      { title: 'Request Submitted', time: 'Feb 22, 2026 at 9:15 AM', done: true },
      { title: 'Confirmed by Two Sisters', time: 'Feb 22, 2026 at 10:30 AM', done: true },
      { title: 'Team Dispatched', time: 'Feb 24, 2026 at 8:00 AM', done: true },
      { title: 'Cleaning In Progress', time: 'Feb 24, 2026 at 8:45 AM', done: true, active: true },
      { title: 'Quality Photos Uploaded', time: '', done: false },
      { title: 'Completed & Ready', time: '', done: false },
    ],
    checklist: [
      { text: 'Kitchen deep clean', done: true },
      { text: 'Bathroom sanitization', done: true },
      { text: 'Carpet cleaning', done: false },
      { text: 'Window washing (interior)', done: false },
      { text: 'Baseboard & trim wipe-down', done: true },
      { text: 'Appliance cleaning (inside)', done: true },
      { text: 'Light fixtures dusted', done: false },
      { text: 'Final walkthrough & photos', done: false },
    ]
  },
  {
    id: 'J002',
    unit: '302',
    building: 'B',
    planId: 'A2',
    type: 'turnover',
    status: 'scheduled',
    priority: 'standard',
    requestedDate: '2026-02-20',
    scheduledDate: '2026-02-26',
    completedDate: null,
    notes: 'Standard turnover clean. New tenant moves in March 1.',
    photos: { before: 0, after: 0 },
    timeline: [
      { title: 'Request Submitted', time: 'Feb 20, 2026 at 2:30 PM', done: true },
      { title: 'Confirmed by Two Sisters', time: 'Feb 20, 2026 at 4:15 PM', done: true },
      { title: 'Scheduled for Feb 26', time: 'Feb 21, 2026 at 9:00 AM', done: true, active: true },
      { title: 'Team Dispatched', time: '', done: false },
      { title: 'Cleaning In Progress', time: '', done: false },
      { title: 'Completed & Ready', time: '', done: false },
    ],
    checklist: [
      { text: 'Kitchen deep clean', done: false },
      { text: 'Bathroom sanitization', done: false },
      { text: 'Carpet cleaning', done: false },
      { text: 'Window washing (interior)', done: false },
      { text: 'Baseboard & trim wipe-down', done: false },
      { text: 'Appliance cleaning (inside)', done: false },
      { text: 'Light fixtures dusted', done: false },
      { text: 'Final walkthrough & photos', done: false },
    ]
  },
  {
    id: 'J003',
    unit: '402',
    building: 'B',
    planId: 'A1',
    type: 'move-in',
    status: 'completed',
    priority: 'standard',
    requestedDate: '2026-02-15',
    scheduledDate: '2026-02-18',
    completedDate: '2026-02-18',
    notes: 'Pre-move-in refresh. Tenant arrives Feb 20.',
    photos: { before: 4, after: 6 },
    timeline: [
      { title: 'Request Submitted', time: 'Feb 15, 2026 at 11:00 AM', done: true },
      { title: 'Confirmed by Two Sisters', time: 'Feb 15, 2026 at 1:20 PM', done: true },
      { title: 'Team Dispatched', time: 'Feb 18, 2026 at 8:30 AM', done: true },
      { title: 'Cleaning In Progress', time: 'Feb 18, 2026 at 9:00 AM', done: true },
      { title: 'Quality Photos Uploaded', time: 'Feb 18, 2026 at 1:45 PM', done: true },
      { title: 'Completed & Ready', time: 'Feb 18, 2026 at 2:00 PM', done: true },
    ],
    checklist: [
      { text: 'Kitchen deep clean', done: true },
      { text: 'Bathroom sanitization', done: true },
      { text: 'Carpet cleaning', done: true },
      { text: 'Window washing (interior)', done: true },
      { text: 'Baseboard & trim wipe-down', done: true },
      { text: 'Appliance cleaning (inside)', done: true },
      { text: 'Light fixtures dusted', done: true },
      { text: 'Final walkthrough & photos', done: true },
    ]
  },
  {
    id: 'J004',
    unit: '203',
    building: 'A',
    planId: 'C1',
    type: 'move-out',
    status: 'completed',
    priority: 'standard',
    requestedDate: '2026-02-12',
    scheduledDate: '2026-02-14',
    completedDate: '2026-02-14',
    notes: 'Long-term tenant (3 years). May need extra attention to walls and fixtures.',
    photos: { before: 5, after: 8 },
    timeline: [
      { title: 'Request Submitted', time: 'Feb 12, 2026 at 3:45 PM', done: true },
      { title: 'Confirmed by Two Sisters', time: 'Feb 12, 2026 at 5:00 PM', done: true },
      { title: 'Team Dispatched', time: 'Feb 14, 2026 at 7:45 AM', done: true },
      { title: 'Cleaning In Progress', time: 'Feb 14, 2026 at 8:15 AM', done: true },
      { title: 'Quality Photos Uploaded', time: 'Feb 14, 2026 at 2:30 PM', done: true },
      { title: 'Completed & Ready', time: 'Feb 14, 2026 at 3:00 PM', done: true },
    ],
    checklist: [
      { text: 'Kitchen deep clean', done: true },
      { text: 'Bathroom sanitization', done: true },
      { text: 'Carpet cleaning', done: true },
      { text: 'Window washing (interior)', done: true },
      { text: 'Baseboard & trim wipe-down', done: true },
      { text: 'Appliance cleaning (inside)', done: true },
      { text: 'Light fixtures dusted', done: true },
      { text: 'Final walkthrough & photos', done: true },
    ]
  },
  {
    id: 'J005',
    unit: '102',
    building: 'A',
    planId: 'A2',
    type: 'move-out',
    status: 'needs-review',
    priority: 'standard',
    requestedDate: '2026-02-08',
    scheduledDate: '2026-02-10',
    completedDate: '2026-02-10',
    notes: 'Standard move-out. Please review photos and approve.',
    photos: { before: 3, after: 6 },
    timeline: [
      { title: 'Request Submitted', time: 'Feb 8, 2026 at 10:00 AM', done: true },
      { title: 'Confirmed by Two Sisters', time: 'Feb 8, 2026 at 11:30 AM', done: true },
      { title: 'Team Dispatched', time: 'Feb 10, 2026 at 8:00 AM', done: true },
      { title: 'Cleaning Completed', time: 'Feb 10, 2026 at 12:30 PM', done: true },
      { title: 'Quality Photos Uploaded', time: 'Feb 10, 2026 at 1:00 PM', done: true },
      { title: 'Awaiting Your Review', time: '', done: false, active: true },
    ],
    checklist: [
      { text: 'Kitchen deep clean', done: true },
      { text: 'Bathroom sanitization', done: true },
      { text: 'Carpet cleaning', done: true },
      { text: 'Window washing (interior)', done: true },
      { text: 'Baseboard & trim wipe-down', done: true },
      { text: 'Appliance cleaning (inside)', done: true },
      { text: 'Light fixtures dusted', done: true },
      { text: 'Final walkthrough & photos', done: true },
    ]
  }
];

const PLACEHOLDER_PHOTOS = [
  { id: 1, label: 'Kitchen', room: 'kitchen' },
  { id: 2, label: 'Kitchen Counter', room: 'kitchen' },
  { id: 3, label: 'Bathroom', room: 'bathroom' },
  { id: 4, label: 'Bathroom Vanity', room: 'bathroom' },
  { id: 5, label: 'Living Room', room: 'living' },
  { id: 6, label: 'Living Room Window', room: 'living' },
  { id: 7, label: 'Bedroom', room: 'bedroom' },
  { id: 8, label: 'Bedroom Closet', room: 'bedroom' },
];

// ============================================================
// HELPER FUNCTIONS
// ============================================================
function getPlan(planId) {
  return FLOOR_PLANS.find(p => p.id === planId) || {};
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getTypeLabel(type) {
  switch (type) {
    case 'move-out': return 'Move-Out Clean';
    case 'move-in': return 'Move-In Clean';
    case 'turnover': return 'Full Turnover';
    default: return type;
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'in-progress': return 'In Progress';
    case 'scheduled': return 'Scheduled';
    case 'completed': return 'Completed';
    case 'pending': return 'Pending';
    case 'needs-review': return 'Needs Review';
    default: return status;
  }
}

// Color generator for placeholder photos
function getPhotoColor(room) {
  const colors = {
    kitchen: '#e8d5c0',
    bathroom: '#c5d8e8',
    living: '#d4ddc5',
    bedroom: '#ddd5e8',
  };
  return colors[room] || '#e0e0e0';
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function PropertyManagerPortal() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [unitSearch, setUnitSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState('all');
  const [unitView, setUnitView] = useState('grid');
  const [photoTab, setPhotoTab] = useState('after');
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Request form state
  const [requestUnit, setRequestUnit] = useState('');
  const [requestType, setRequestType] = useState('move-out');
  const [requestPriority, setRequestPriority] = useState('standard');
  const [requestDate, setRequestDate] = useState('');
  const [requestMoveInDate, setRequestMoveInDate] = useState('');
  const [requestNotes, setRequestNotes] = useState('');

  const pendingCount = MOCK_JOBS.filter(j => j.status === 'pending' || j.status === 'needs-review').length;
  const activeCount = MOCK_JOBS.filter(j => j.status === 'in-progress' || j.status === 'scheduled').length;

  // Filter units
  const filteredUnits = UNITS.filter(u => {
    const plan = getPlan(u.planId);
    const matchesSearch = !unitSearch ||
      u.number.toLowerCase().includes(unitSearch.toLowerCase()) ||
      plan.name?.toLowerCase().includes(unitSearch.toLowerCase()) ||
      (u.tenant && u.tenant.toLowerCase().includes(unitSearch.toLowerCase()));
    const matchesFilter = unitFilter === 'all' || u.status === unitFilter;
    return matchesSearch && matchesFilter;
  });

  function handleSubmitRequest(e) {
    e.preventDefault();
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setActiveTab('jobs');
      setRequestUnit('');
      setRequestType('move-out');
      setRequestPriority('standard');
      setRequestDate('');
      setRequestMoveInDate('');
      setRequestNotes('');
    }, 2500);
  }

  return (
    <div className="pm-portal">
      {/* TOP NAVIGATION */}
      <nav className="pm-topnav">
        <div className="pm-topnav-left">
          <div className="pm-logo">
            <div className="pm-logo-icon">
              <Sparkles size={20} />
            </div>
            <div>
              <div className="pm-logo-text">Two Sisters</div>
              <span className="pm-logo-sub">Property Portal</span>
            </div>
          </div>
          <div className="pm-nav-tabs">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
              { id: 'units', label: 'Units', icon: Building2 },
              { id: 'request', label: 'Request Clean', icon: ClipboardList },
              { id: 'jobs', label: 'Jobs', icon: FileText, badge: pendingCount || null },
            ].map(tab => (
              <button
                key={tab.id}
                className={`pm-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => { setActiveTab(tab.id); setSelectedJob(null); }}
              >
                <tab.icon size={16} />
                {tab.label}
                {tab.badge && <span className="tab-badge">{tab.badge}</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="pm-topnav-right">
          <button className="pm-notif-btn">
            <Bell size={18} />
            <span className="pm-notif-dot" />
          </button>
          <div className="pm-user-info">
            <div className="pm-avatar">JT</div>
            <div>
              <div className="pm-user-name">{PROPERTY.manager}</div>
              <span className="pm-user-role">Property Manager</span>
            </div>
          </div>
        </div>
      </nav>

      {/* PROPERTY HEADER */}
      <div className="pm-property-header">
        <div className="pm-property-info">
          <div className="pm-property-icon">
            <Building2 size={24} />
          </div>
          <div>
            <div className="pm-property-name">{PROPERTY.name}</div>
            <div className="pm-property-address">{PROPERTY.address}</div>
          </div>
        </div>
        <div className="pm-property-actions">
          <button className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => setActiveTab('units')}>
            <Building2 size={14} />
            {PROPERTY.totalUnits} Units
          </button>
          <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={() => setActiveTab('request')}>
            <Plus size={14} />
            New Request
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="pm-main">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'units' && renderUnits()}
        {activeTab === 'request' && renderRequestForm()}
        {activeTab === 'jobs' && !selectedJob && renderJobs()}
        {activeTab === 'jobs' && selectedJob && renderJobDetail()}
      </div>

      {/* ADD UNIT MODAL */}
      {showAddUnit && renderAddUnitModal()}

      {/* SUCCESS OVERLAY */}
      {showSuccess && (
        <div className="pm-success-overlay">
          <div className="pm-success-card">
            <div className="pm-success-icon">
              <CheckCircle2 size={32} />
            </div>
            <div className="pm-success-title">Request Submitted!</div>
            <div className="pm-success-desc">
              Your cleaning request has been sent to Two Sisters Cleaning.
              You'll receive a confirmation shortly.
            </div>
          </div>
        </div>
      )}

      {/* PHOTO LIGHTBOX */}
      {lightboxPhoto !== null && (
        <div className="pm-lightbox" onClick={() => setLightboxPhoto(null)}>
          <button className="pm-lightbox-close" onClick={() => setLightboxPhoto(null)}>
            <X size={20} />
          </button>
          <div
            style={{
              width: '70%',
              maxWidth: 800,
              aspectRatio: '4/3',
              borderRadius: 'var(--radius-md)',
              background: getPhotoColor(PLACEHOLDER_PHOTOS[lightboxPhoto % PLACEHOLDER_PHOTOS.length].room),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 8
            }}
            onClick={e => e.stopPropagation()}
          >
            <Camera size={48} color="#666" />
            <span style={{ color: '#666', fontSize: '1rem', fontWeight: 500 }}>
              {PLACEHOLDER_PHOTOS[lightboxPhoto % PLACEHOLDER_PHOTOS.length].label}
            </span>
          </div>
          <div className="pm-lightbox-caption">
            Photo {lightboxPhoto + 1} of {PLACEHOLDER_PHOTOS.length} — {PLACEHOLDER_PHOTOS[lightboxPhoto % PLACEHOLDER_PHOTOS.length].label}
          </div>
        </div>
      )}
    </div>
  );

  // ============================================================
  // DASHBOARD VIEW
  // ============================================================
  function renderDashboard() {
    return (
      <>
        {/* Stats */}
        <div className="pm-stats-row">
          <div className="pm-stat-card">
            <div className="pm-stat-icon sage"><Building2 size={22} /></div>
            <div className="pm-stat-content">
              <div className="pm-stat-label">Total Units</div>
              <div className="pm-stat-value">{UNITS.length}</div>
              <div className="pm-stat-change up"><TrendingUp size={12} /> 3 floor plans</div>
            </div>
          </div>
          <div className="pm-stat-card">
            <div className="pm-stat-icon orange"><AlertCircle size={22} /></div>
            <div className="pm-stat-content">
              <div className="pm-stat-label">Pending Review</div>
              <div className="pm-stat-value">{pendingCount}</div>
              <div className="pm-stat-change" style={{ color: 'var(--orange)' }}>Needs your attention</div>
            </div>
          </div>
          <div className="pm-stat-card">
            <div className="pm-stat-icon blue"><Clock size={22} /></div>
            <div className="pm-stat-content">
              <div className="pm-stat-label">Active Cleans</div>
              <div className="pm-stat-value">{activeCount}</div>
              <div className="pm-stat-change up">On schedule</div>
            </div>
          </div>
          <div className="pm-stat-card">
            <div className="pm-stat-icon green"><CheckCircle2 size={22} /></div>
            <div className="pm-stat-content">
              <div className="pm-stat-label">Completed (Feb)</div>
              <div className="pm-stat-value">{MOCK_JOBS.filter(j => j.status === 'completed').length}</div>
              <div className="pm-stat-change up"><TrendingUp size={12} /> +2 from Jan</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="pm-section-header">
          <div>
            <div className="pm-section-title">Quick Actions</div>
          </div>
        </div>
        <div className="pm-quick-actions">
          <button className="pm-quick-action" onClick={() => setActiveTab('request')}>
            <div className="pm-quick-action-icon sage"><DoorOpen size={24} /></div>
            <div className="pm-quick-action-label">Request Move-Out Clean</div>
            <div className="pm-quick-action-desc">Schedule a move-out deep clean</div>
          </button>
          <button className="pm-quick-action" onClick={() => setActiveTab('request')}>
            <div className="pm-quick-action-icon gold"><DoorClosed size={24} /></div>
            <div className="pm-quick-action-label">Request Move-In Clean</div>
            <div className="pm-quick-action-desc">Prep a unit for new tenant</div>
          </button>
          <button className="pm-quick-action" onClick={() => setActiveTab('request')}>
            <div className="pm-quick-action-icon blue"><ArrowRightLeft size={24} /></div>
            <div className="pm-quick-action-label">Full Turnover</div>
            <div className="pm-quick-action-desc">Complete move-out + move-in clean</div>
          </button>
        </div>

        {/* Dashboard Grid */}
        <div className="pm-dashboard-grid">
          <div className="pm-dashboard-main">
            {/* Recent Jobs */}
            <div className="pm-card">
              <div className="pm-card-header">
                <div className="pm-card-title">Recent Cleaning Requests</div>
                <button className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => setActiveTab('jobs')}>
                  View All <ChevronRight size={14} />
                </button>
              </div>
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Photos</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {MOCK_JOBS.slice(0, 4).map(job => {
                    const plan = getPlan(job.planId);
                    return (
                      <tr key={job.id} onClick={() => { setActiveTab('jobs'); setSelectedJob(job); }} style={{ cursor: 'pointer' }}>
                        <td>
                          <div className="unit-cell">
                            <div className="pm-unit-badge">{job.unit}</div>
                            <div className="pm-unit-info">
                              <span className="pm-unit-number">Unit {job.unit}</span>
                              <span className="pm-unit-floor">{plan.name}</span>
                            </div>
                          </div>
                        </td>
                        <td>{getTypeLabel(job.type)}</td>
                        <td><span className={`pm-status ${job.status}`}><span className="pm-status-dot" />{getStatusLabel(job.status)}</span></td>
                        <td>{formatDate(job.scheduledDate)}</td>
                        <td>
                          <span className="pm-job-photos">
                            <Camera size={14} />
                            {job.photos.before + job.photos.after}
                          </span>
                        </td>
                        <td><ChevronRight size={16} color="#ccc" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Units Needing Attention */}
            <div className="pm-card">
              <div className="pm-card-header">
                <div className="pm-card-title">Units in Turnover</div>
                <button className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => { setActiveTab('units'); setUnitFilter('turnover'); }}>
                  View All <ChevronRight size={14} />
                </button>
              </div>
              <div className="pm-card-body">
                {UNITS.filter(u => u.status === 'turnover').map(unit => {
                  const plan = getPlan(unit.planId);
                  return (
                    <div key={unit.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="pm-unit-badge">{unit.number}</div>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: '0.9rem' }}>Unit {unit.number} — Bldg {unit.building}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{plan.name} | {plan.beds}BR / {plan.baths}BA</div>
                        </div>
                      </div>
                      <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={(e) => { e.stopPropagation(); setActiveTab('request'); setRequestUnit(unit.id); }}>
                        Request Clean
                      </button>
                    </div>
                  );
                })}
                {UNITS.filter(u => u.status === 'turnover').length === 0 && (
                  <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No units in turnover</div>
                )}
              </div>
            </div>
          </div>

          <div className="pm-dashboard-side">
            {/* Activity Feed */}
            <div className="pm-card">
              <div className="pm-card-header">
                <div className="pm-card-title">Recent Activity</div>
              </div>
              <div className="pm-card-body" style={{ padding: '16px 24px' }}>
                <div className="pm-activity-item">
                  <div className="pm-activity-icon photo"><Camera size={16} /></div>
                  <div className="pm-activity-content">
                    <div className="pm-activity-text"><strong>Unit 102</strong> — After photos uploaded for review</div>
                    <div className="pm-activity-time">20 min ago</div>
                  </div>
                </div>
                <div className="pm-activity-item">
                  <div className="pm-activity-icon clean"><Sparkles size={16} /></div>
                  <div className="pm-activity-content">
                    <div className="pm-activity-text"><strong>Unit 103</strong> — Move-out clean started by cleaning team</div>
                    <div className="pm-activity-time">2 hours ago</div>
                  </div>
                </div>
                <div className="pm-activity-item">
                  <div className="pm-activity-icon complete"><CheckCircle2 size={16} /></div>
                  <div className="pm-activity-content">
                    <div className="pm-activity-text"><strong>Unit 402</strong> — Move-in clean completed</div>
                    <div className="pm-activity-time">Feb 18 at 2:00 PM</div>
                  </div>
                </div>
                <div className="pm-activity-item">
                  <div className="pm-activity-icon complete"><CheckCircle2 size={16} /></div>
                  <div className="pm-activity-content">
                    <div className="pm-activity-text"><strong>Unit 203</strong> — Move-out clean completed & approved</div>
                    <div className="pm-activity-time">Feb 14 at 3:00 PM</div>
                  </div>
                </div>
                <div className="pm-activity-item">
                  <div className="pm-activity-icon alert"><AlertCircle size={16} /></div>
                  <div className="pm-activity-content">
                    <div className="pm-activity-text"><strong>Unit 302</strong> — Turnover clean scheduled for Feb 26</div>
                    <div className="pm-activity-time">Feb 21 at 9:00 AM</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="pm-card">
              <div className="pm-card-header">
                <div className="pm-card-title">Your Cleaning Team</div>
              </div>
              <div className="pm-card-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--sage)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>TS</div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>Two Sisters Cleaning Co.</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Property Services Team</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <Phone size={14} /> (317) 555-0198
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <Mail size={14} /> property@twosisterscleaningco.com
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <Clock size={14} /> Response within 2 hours
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ============================================================
  // UNITS VIEW
  // ============================================================
  function renderUnits() {
    return (
      <>
        <div className="pm-section-header">
          <div>
            <div className="pm-section-title">Unit Management</div>
            <div className="pm-section-subtitle">Manage your property units and floor plans</div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="pm-units-toolbar">
          <div className="pm-search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search units, floor plans, tenants..."
              value={unitSearch}
              onChange={e => setUnitSearch(e.target.value)}
            />
          </div>
          <div className="pm-toolbar-actions">
            {['all', 'occupied', 'vacant', 'turnover'].map(f => (
              <button
                key={f}
                className={`pm-filter-btn ${unitFilter === f ? 'active' : ''}`}
                onClick={() => setUnitFilter(f)}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                {f !== 'all' && (
                  <span style={{ fontWeight: 700, marginLeft: 4 }}>
                    {UNITS.filter(u => u.status === f).length}
                  </span>
                )}
              </button>
            ))}
            <button className={`pm-filter-btn ${unitView === 'grid' ? 'active' : ''}`} onClick={() => setUnitView('grid')}>
              <LayoutGrid size={16} />
            </button>
            <button className={`pm-filter-btn ${unitView === 'list' ? 'active' : ''}`} onClick={() => setUnitView('list')}>
              <List size={16} />
            </button>
            <button className="pm-btn pm-btn-outline pm-btn-sm" onClick={() => setShowAddUnit(true)}>
              <Upload size={14} /> Import
            </button>
            <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={() => setShowAddUnit(true)}>
              <Plus size={14} /> Add Unit
            </button>
          </div>
        </div>

        {/* Floor Plan Summary */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
          {FLOOR_PLANS.map(plan => (
            <div key={plan.id} style={{
              background: 'white',
              borderRadius: 'var(--radius-md)',
              padding: '14px 20px',
              boxShadow: 'var(--shadow-sm)',
              minWidth: 180,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: 'var(--sage-pale)', color: 'var(--sage)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.75rem' }}>
                {plan.id}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--navy)' }}>{plan.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {plan.beds}BR / {plan.baths}BA · {plan.sqft} sqft
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Unit Grid or Table */}
        {unitView === 'grid' ? (
          <div className="pm-units-grid">
            {filteredUnits.map(unit => {
              const plan = getPlan(unit.planId);
              return (
                <div key={unit.id} className="pm-unit-card">
                  <div className="pm-unit-card-top">
                    <span className="pm-unit-card-number">Unit {unit.number}</span>
                    <span className={`pm-status ${unit.status}`}>
                      <span className="pm-status-dot" />
                      {unit.status.charAt(0).toUpperCase() + unit.status.slice(1)}
                    </span>
                  </div>
                  <div className="pm-unit-card-details">
                    <div className="pm-unit-card-detail">
                      <Home size={14} />{plan.name} ({plan.id})
                    </div>
                    <div className="pm-unit-card-detail">
                      <BedDouble size={14} />{plan.beds} Bed / {plan.baths} Bath
                    </div>
                    <div className="pm-unit-card-detail">
                      <Ruler size={14} />{plan.sqft} sqft
                    </div>
                    <div className="pm-unit-card-detail">
                      <MapPin size={14} />Building {unit.building}, Floor {unit.floor}
                    </div>
                    {unit.tenant && (
                      <div className="pm-unit-card-detail">
                        <User size={14} />{unit.tenant}
                      </div>
                    )}
                  </div>
                  <div className="pm-unit-card-footer">
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {unit.lastCleaned ? `Last cleaned ${formatDate(unit.lastCleaned)}` : 'Not yet cleaned'}
                    </span>
                    {(unit.status === 'vacant' || unit.status === 'turnover') && (
                      <span className="pm-unit-card-action" onClick={() => { setActiveTab('request'); setRequestUnit(unit.id); }}>
                        Request Clean <ChevronRight size={14} />
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="pm-card">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>Unit</th>
                  <th>Floor Plan</th>
                  <th>Beds / Baths</th>
                  <th>Sq Ft</th>
                  <th>Building</th>
                  <th>Status</th>
                  <th>Tenant</th>
                  <th>Last Cleaned</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredUnits.map(unit => {
                  const plan = getPlan(unit.planId);
                  return (
                    <tr key={unit.id}>
                      <td>
                        <div className="unit-cell">
                          <div className="pm-unit-badge">{unit.number}</div>
                          <span className="pm-unit-number">Unit {unit.number}</span>
                        </div>
                      </td>
                      <td>{plan.name} ({plan.id})</td>
                      <td>{plan.beds} / {plan.baths}</td>
                      <td>{plan.sqft}</td>
                      <td>Bldg {unit.building}, Fl {unit.floor}</td>
                      <td><span className={`pm-status ${unit.status}`}><span className="pm-status-dot" />{unit.status.charAt(0).toUpperCase() + unit.status.slice(1)}</span></td>
                      <td>{unit.tenant || '—'}</td>
                      <td>{unit.lastCleaned ? formatDate(unit.lastCleaned) : '—'}</td>
                      <td>
                        {(unit.status === 'vacant' || unit.status === 'turnover') && (
                          <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={() => { setActiveTab('request'); setRequestUnit(unit.id); }}>
                            Request
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  // ============================================================
  // REQUEST FORM
  // ============================================================
  function renderRequestForm() {
    return (
      <div className="pm-request-page">
        <form className="pm-form-card" onSubmit={handleSubmitRequest}>
          <div className="pm-form-header">
            <h2>Request a Cleaning</h2>
            <p>Submit a move-out, move-in, or turnover cleaning request</p>
          </div>
          <div className="pm-form-body">
            {/* Unit Selection */}
            <div className="pm-form-group">
              <label>Select Unit</label>
              <select
                className="pm-form-select"
                value={requestUnit}
                onChange={e => setRequestUnit(e.target.value)}
                required
              >
                <option value="">Choose a unit...</option>
                {UNITS.map(u => {
                  const plan = getPlan(u.planId);
                  return (
                    <option key={u.id} value={u.id}>
                      Unit {u.number} — {plan.name} ({plan.beds}BR/{plan.baths}BA) — Bldg {u.building}
                      {u.status === 'turnover' ? ' [TURNOVER]' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Clean Type */}
            <div className="pm-form-group">
              <label>Type of Clean</label>
              <div className="pm-type-selector">
                <div
                  className={`pm-type-option ${requestType === 'move-out' ? 'selected' : ''}`}
                  onClick={() => setRequestType('move-out')}
                >
                  <div className="pm-type-option-icon"><DoorOpen size={20} /></div>
                  <div className="pm-type-option-label">Move-Out</div>
                  <div className="pm-type-option-desc">Deep clean after tenant vacates</div>
                </div>
                <div
                  className={`pm-type-option ${requestType === 'move-in' ? 'selected' : ''}`}
                  onClick={() => setRequestType('move-in')}
                >
                  <div className="pm-type-option-icon"><DoorClosed size={20} /></div>
                  <div className="pm-type-option-label">Move-In</div>
                  <div className="pm-type-option-desc">Prep & refresh for new tenant</div>
                </div>
                <div
                  className={`pm-type-option ${requestType === 'turnover' ? 'selected' : ''}`}
                  onClick={() => setRequestType('turnover')}
                >
                  <div className="pm-type-option-icon"><ArrowRightLeft size={20} /></div>
                  <div className="pm-type-option-label">Full Turnover</div>
                  <div className="pm-type-option-desc">Complete move-out & move-in</div>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="pm-form-row">
              <div className="pm-form-group">
                <label>
                  {requestType === 'move-in' ? 'Move-In Date' : 'Preferred Clean Date'}
                  <span className="label-hint"> (or ASAP)</span>
                </label>
                <input
                  type="date"
                  className="pm-form-input"
                  value={requestDate}
                  onChange={e => setRequestDate(e.target.value)}
                />
              </div>
              {requestType === 'turnover' && (
                <div className="pm-form-group">
                  <label>New Tenant Move-In Date</label>
                  <input
                    type="date"
                    className="pm-form-input"
                    value={requestMoveInDate}
                    onChange={e => setRequestMoveInDate(e.target.value)}
                  />
                </div>
              )}
              {requestType !== 'turnover' && (
                <div className="pm-form-group">
                  <label>Priority</label>
                  <div className="pm-priority-selector">
                    {['standard', 'urgent', 'emergency'].map(p => (
                      <div
                        key={p}
                        className={`pm-priority-option ${p} ${requestPriority === p ? 'selected' : ''}`}
                        onClick={() => setRequestPriority(p)}
                      >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="pm-form-group">
              <label>
                Special Instructions <span className="label-hint">(optional)</span>
              </label>
              <textarea
                className="pm-form-textarea"
                placeholder="Pets in the unit, specific areas of concern, access codes, etc."
                value={requestNotes}
                onChange={e => setRequestNotes(e.target.value)}
              />
            </div>
          </div>
          <div className="pm-form-footer">
            <button type="button" className="pm-btn pm-btn-secondary" onClick={() => setActiveTab('dashboard')}>
              Cancel
            </button>
            <button type="submit" className="pm-btn pm-btn-primary">
              <ClipboardList size={16} />
              Submit Request
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ============================================================
  // JOBS LIST
  // ============================================================
  function renderJobs() {
    return (
      <>
        <div className="pm-section-header">
          <div>
            <div className="pm-section-title">Cleaning Jobs</div>
            <div className="pm-section-subtitle">Track all your cleaning requests and their progress</div>
          </div>
          <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={() => setActiveTab('request')}>
            <Plus size={14} /> New Request
          </button>
        </div>

        <div className="pm-jobs-list">
          {MOCK_JOBS.map(job => {
            const plan = getPlan(job.planId);
            return (
              <div key={job.id} className="pm-job-card" onClick={() => setSelectedJob(job)}>
                <div className={`pm-job-icon ${job.type}`}>
                  {job.type === 'move-out' && <DoorOpen size={22} />}
                  {job.type === 'move-in' && <DoorClosed size={22} />}
                  {job.type === 'turnover' && <ArrowRightLeft size={22} />}
                </div>
                <div className="pm-job-main">
                  <div className="pm-job-title">{getTypeLabel(job.type)} — Unit {job.unit}</div>
                  <div className="pm-job-meta">
                    <span className="pm-job-meta-item"><Home size={14} />{plan.name}</span>
                    <span className="pm-job-meta-item"><MapPin size={14} />Bldg {job.building}</span>
                    <span className="pm-job-meta-item"><Calendar size={14} />{formatDate(job.scheduledDate)}</span>
                    {job.priority === 'urgent' && (
                      <span className="pm-status turnover"><span className="pm-status-dot" />Urgent</span>
                    )}
                  </div>
                </div>
                <div className="pm-job-right">
                  <span className={`pm-status ${job.status}`}>
                    <span className="pm-status-dot" />{getStatusLabel(job.status)}
                  </span>
                  <span className="pm-job-photos">
                    <Camera size={14} />
                    {job.photos.before + job.photos.after}
                  </span>
                  <ChevronRight size={18} className="pm-job-arrow" />
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  // ============================================================
  // JOB DETAIL VIEW
  // ============================================================
  function renderJobDetail() {
    const job = selectedJob;
    const plan = getPlan(job.planId);
    const totalPhotos = job.photos.before + job.photos.after;
    const doneCount = job.checklist.filter(c => c.done).length;

    return (
      <>
        <button className="pm-back-link" onClick={() => setSelectedJob(null)}>
          <ArrowLeft size={16} /> Back to Jobs
        </button>

        <div className="pm-job-detail">
          <div className="pm-job-detail-main">
            {/* Header Card */}
            <div className="pm-job-detail-header">
              <div className="pm-job-detail-title">
                <div className={`pm-job-icon ${job.type}`}>
                  {job.type === 'move-out' && <DoorOpen size={22} />}
                  {job.type === 'move-in' && <DoorClosed size={22} />}
                  {job.type === 'turnover' && <ArrowRightLeft size={22} />}
                </div>
                <div>
                  <h2>{getTypeLabel(job.type)} — Unit {job.unit}</h2>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    {plan.name} ({plan.id}) · Bldg {job.building} · {plan.beds}BR/{plan.baths}BA · {plan.sqft} sqft
                  </div>
                </div>
                <span className={`pm-status ${job.status}`} style={{ marginLeft: 'auto' }}>
                  <span className="pm-status-dot" />{getStatusLabel(job.status)}
                </span>
              </div>
              <div className="pm-job-detail-info">
                <div className="pm-info-item">
                  <span className="pm-info-label">Requested</span>
                  <span className="pm-info-value">{formatDate(job.requestedDate)}</span>
                </div>
                <div className="pm-info-item">
                  <span className="pm-info-label">Scheduled</span>
                  <span className="pm-info-value">{formatDate(job.scheduledDate)}</span>
                </div>
                <div className="pm-info-item">
                  <span className="pm-info-label">Completed</span>
                  <span className="pm-info-value">{job.completedDate ? formatDate(job.completedDate) : 'In progress'}</span>
                </div>
                <div className="pm-info-item">
                  <span className="pm-info-label">Priority</span>
                  <span className="pm-info-value" style={{ textTransform: 'capitalize' }}>{job.priority}</span>
                </div>
                <div className="pm-info-item">
                  <span className="pm-info-label">Photos</span>
                  <span className="pm-info-value">{totalPhotos} uploaded</span>
                </div>
                <div className="pm-info-item">
                  <span className="pm-info-label">Checklist</span>
                  <span className="pm-info-value">{doneCount}/{job.checklist.length} complete</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {job.notes && (
              <div className="pm-card">
                <div className="pm-card-header">
                  <div className="pm-card-title">Notes & Instructions</div>
                </div>
                <div className="pm-card-body">
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{job.notes}</p>
                </div>
              </div>
            )}

            {/* Photos */}
            <div className="pm-photos-card">
              <div className="pm-card-header">
                <div className="pm-card-title">Cleaning Photos</div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{totalPhotos} photos</span>
              </div>
              {totalPhotos > 0 ? (
                <>
                  <div className="pm-photos-tabs">
                    <button
                      className={`pm-photos-tab ${photoTab === 'before' ? 'active' : ''}`}
                      onClick={() => setPhotoTab('before')}
                    >
                      Before ({job.photos.before})
                    </button>
                    <button
                      className={`pm-photos-tab ${photoTab === 'after' ? 'active' : ''}`}
                      onClick={() => setPhotoTab('after')}
                    >
                      After ({job.photos.after})
                    </button>
                  </div>
                  <div className="pm-photos-grid">
                    {PLACEHOLDER_PHOTOS.slice(0, photoTab === 'before' ? job.photos.before : job.photos.after).map((photo, i) => (
                      <div key={i} className="pm-photo-thumb" onClick={() => setLightboxPhoto(i)}>
                        <div className="pm-photo-placeholder" style={{ background: getPhotoColor(photo.room) }}>
                          <Camera size={24} />
                          <span>{photo.label}</span>
                        </div>
                      </div>
                    ))}
                    {((photoTab === 'before' && job.photos.before === 0) || (photoTab === 'after' && job.photos.after === 0)) && (
                      <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                        <Camera size={24} style={{ marginBottom: 8 }} />
                        <div style={{ fontSize: '0.85rem' }}>No {photoTab} photos yet</div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  <Camera size={32} style={{ marginBottom: 8 }} />
                  <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>Photos will appear here</div>
                  <div style={{ fontSize: '0.8rem' }}>The cleaning team will upload before & after photos</div>
                </div>
              )}
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="pm-job-detail-side">
            {/* Timeline */}
            <div className="pm-card">
              <div className="pm-card-header">
                <div className="pm-card-title">Progress Timeline</div>
              </div>
              <div className="pm-card-body">
                <div className="pm-timeline">
                  {job.timeline.map((step, i) => (
                    <div key={i} className="pm-timeline-item">
                      <div className={`pm-timeline-dot ${step.done ? 'filled' : ''} ${step.active ? 'active' : ''}`} />
                      <div className="pm-timeline-content">
                        <div className="pm-timeline-title" style={{ color: step.done ? 'var(--navy)' : 'var(--text-muted)' }}>
                          {step.title}
                        </div>
                        {step.time && <div className="pm-timeline-time">{step.time}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Checklist */}
            <div className="pm-card">
              <div className="pm-card-header">
                <div className="pm-card-title">Cleaning Checklist</div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{doneCount}/{job.checklist.length}</span>
              </div>
              <div className="pm-card-body" style={{ padding: '8px 24px 16px' }}>
                <ul className="pm-checklist">
                  {job.checklist.map((item, i) => (
                    <li key={i} className="pm-checklist-item">
                      <div className={`pm-checklist-check ${item.done ? 'done' : ''}`}>
                        {item.done && <Check size={12} />}
                      </div>
                      <span style={{ color: item.done ? 'var(--navy)' : 'var(--text-muted)', textDecoration: item.done ? 'none' : 'none' }}>
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Actions */}
            {job.status === 'needs-review' && (
              <div className="pm-card">
                <div className="pm-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button className="pm-btn pm-btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                    <CheckCircle2 size={16} /> Approve & Close
                  </button>
                  <button className="pm-btn pm-btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
                    <AlertCircle size={16} /> Request Re-Clean
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ============================================================
  // ADD UNIT MODAL
  // ============================================================
  function renderAddUnitModal() {
    return (
      <div className="pm-modal-overlay" onClick={() => setShowAddUnit(false)}>
        <div className="pm-modal" onClick={e => e.stopPropagation()}>
          <div className="pm-modal-header">
            <h3>Add Units</h3>
            <button className="pm-modal-close" onClick={() => setShowAddUnit(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="pm-modal-body">
            {/* CSV Upload */}
            <div className="pm-upload-area" style={{ marginBottom: 24 }}>
              <div className="pm-upload-icon"><Upload size={22} /></div>
              <div className="pm-upload-text">Upload CSV or Excel file</div>
              <div className="pm-upload-hint">Drag & drop or click to browse. Columns: Unit Number, Floor Plan, Building, Floor</div>
            </div>

            <div style={{ textAlign: 'center', margin: '20px 0', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 500 }}>
              — or add manually —
            </div>

            {/* Manual Add */}
            <div className="pm-form-group">
              <label>Unit Number</label>
              <input className="pm-form-input" placeholder="e.g., 501" />
            </div>
            <div className="pm-form-row">
              <div className="pm-form-group">
                <label>Floor Plan</label>
                <select className="pm-form-select">
                  <option value="">Select floor plan...</option>
                  {FLOOR_PLANS.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.id}) — {p.beds}BR/{p.baths}BA</option>
                  ))}
                </select>
              </div>
              <div className="pm-form-group">
                <label>Building</label>
                <input className="pm-form-input" placeholder="e.g., A" />
              </div>
            </div>
            <div className="pm-form-row">
              <div className="pm-form-group">
                <label>Floor</label>
                <input className="pm-form-input" type="number" placeholder="e.g., 5" />
              </div>
              <div className="pm-form-group">
                <label>Status</label>
                <select className="pm-form-select">
                  <option value="occupied">Occupied</option>
                  <option value="vacant">Vacant</option>
                  <option value="turnover">Turnover</option>
                </select>
              </div>
            </div>
          </div>
          <div className="pm-modal-footer">
            <button className="pm-btn pm-btn-secondary" onClick={() => setShowAddUnit(false)}>
              Cancel
            </button>
            <button className="pm-btn pm-btn-primary" onClick={() => setShowAddUnit(false)}>
              <Plus size={16} /> Add Unit
            </button>
          </div>
        </div>
      </div>
    );
  }
}
